import { CreativeService } from '@/services'
import PreReviewService from '@/services/PreReviewService'
import { getResponse } from '@/plugins/store'

export default {
  /**
   * Upload video creative
   */
  async uploadVideoCreative({ commit, state }) {
    commit('UPLOAD_CREATIVE_REQUEST')

    try {
      const form = state.uploadForm
      const { advertiserId } = form

      if (!advertiserId) {
        throw new Error('No advertiser account selected')
      }

      const uploadParams = {
        upload_type: form.uploadMethod,
        advertiser_id: advertiserId,
        flaw_detect: form.flawDetect,
        auto_fix_enabled: form.autoFixEnabled,
        auto_bind_enabled: form.autoBindEnabled,
      }

      // Add method-specific parameters
      switch (form.uploadMethod) {
        case 'UPLOAD_BY_FILE':
          uploadParams.video_file = form.file
          break
        case 'UPLOAD_BY_URL':
          uploadParams.video_url = form.url
          break
        case 'UPLOAD_BY_FILE_ID':
          uploadParams.file_id = form.fileId
          break
        case 'UPLOAD_BY_VIDEO_ID':
          uploadParams.video_id = form.videoId
          break
        default:
          throw new Error('Invalid upload method')
      }

      const { data } = await CreativeService.uploadVideoCreative(uploadParams, {
        onUploadProgress: progressEvent => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          commit('UPLOAD_CREATIVE_PROGRESS', progress)
        },
      })

      const creative = getResponse(data)
      commit('UPLOAD_CREATIVE_SUCCESS', creative)
      return creative
    } catch (error) {
      commit('UPLOAD_CREATIVE_FAILURE', error)
      throw error
    }
  },

  /**
   * Upload image creative
   */
  async uploadImageCreative({ commit, state }) {
    commit('UPLOAD_CREATIVE_REQUEST')

    try {
      const form = state.uploadForm
      const { advertiserId } = form

      if (!advertiserId) {
        throw new Error('No advertiser account selected')
      }

      const uploadParams = {
        upload_type: form.uploadMethod,
        advertiser_id: advertiserId,
      }

      // Add method-specific parameters
      switch (form.uploadMethod) {
        case 'UPLOAD_BY_FILE':
          uploadParams.image_file = form.file
          break
        case 'UPLOAD_BY_URL':
          uploadParams.image_url = form.url
          break
        case 'UPLOAD_BY_FILE_ID':
          uploadParams.file_id = form.fileId
          break
        default:
          throw new Error('Invalid upload method')
      }

      const { data } = await CreativeService.uploadImageCreative(uploadParams, {
        onUploadProgress: progressEvent => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          commit('UPLOAD_CREATIVE_PROGRESS', progress)
        },
      })

      const creative = getResponse(data)
      commit('UPLOAD_CREATIVE_SUCCESS', creative)
      return creative
    } catch (error) {
      commit('UPLOAD_CREATIVE_FAILURE', error)
      throw error
    }
  },

  /**
   * Submit creative for pre-review
   */
  async submitPreReview({ commit, state }, creativeId) {
    commit('SUBMIT_PRE_REVIEW_REQUEST')

    try {
      const form = state.uploadForm
      const { data } = await CreativeService.submitPreReview({
        creative_id: creativeId,
        ad_text: form.adText,
        landing_page_url: form.landingPageUrl,
        location_codes: form.locationCodes,
        is_ecommerce: form.isEcommerce,
      })

      const result = getResponse(data)
      commit('SUBMIT_PRE_REVIEW_SUCCESS', result)
      return result
    } catch (error) {
      commit('SUBMIT_PRE_REVIEW_FAILURE', error)
      throw error
    }
  },

  /**
   * Fetch creatives list
   */
  async fetchCreatives({ commit, state }) {
    commit('FETCH_CREATIVES_REQUEST')

    try {
      const { advertiserId } = state.uploadForm
      const params = {
        advertiser_id: advertiserId,
        page: state.creativesPage,
        limit: state.creativesPageSize, // Changed from page_size to limit
        sort_by: state.creativesSort.sortBy,
        sort_desc: state.creativesSort.sortDesc,
      }

      // Add filters if they have values
      if (state.creativesFilters.search) {
        params.search = state.creativesFilters.search
      }
      if (state.creativesFilters.type) {
        params.type = state.creativesFilters.type
      }
      if (state.creativesFilters.status) {
        params.status = state.creativesFilters.status
      }
      if (state.creativesFilters.preReviewStatus) {
        params.pre_review_status = state.creativesFilters.preReviewStatus
      }
      if (state.creativesFilters.dateRange && state.creativesFilters.dateRange.length === 2) {
        const [dateFrom, dateTo] = state.creativesFilters.dateRange
        params.date_from = dateFrom
        params.date_to = dateTo
      }

      const { data } = await CreativeService.getCreatives(params)

      // Handle the new response structure
      const response = getResponse(data)
      commit('FETCH_CREATIVES_SUCCESS', {
        creatives: response.creatives || [], // Changed from response.list to response.creatives
        total: response.total || 0,
        page: response.page || 1,
      })
    } catch (error) {
      commit('FETCH_CREATIVES_FAILURE', error)
      throw error
    }
  },

  /**
   * Get creative status
   */
  async getCreativeStatus({ commit }, creativeId) {
    const { data } = await CreativeService.getCreativeStatus(creativeId)
    const result = getResponse(data)
    commit('UPDATE_CREATIVE_STATUS', {
      creativeId,
      status: result.status,
    })
    return result
  },

  /**
   * Delete creative
   */
  async deleteCreative({ commit }, creativeId) {
    commit('DELETE_CREATIVE_REQUEST')

    try {
      await CreativeService.deleteCreative(creativeId)
      commit('DELETE_CREATIVE_SUCCESS', creativeId)
    } catch (error) {
      commit('DELETE_CREATIVE_FAILURE', error)
      throw error
    }
  },

  /**
   * Validate file
   */
  validateFile({ commit }, { file, type }) {
    const validation = CreativeService.validateFile(file, type)
    commit('SET_FILE_VALIDATION', validation)
    return validation
  },

  /**
   * Update form data
   */
  updateForm({ commit }, formData) {
    commit('SET_UPLOAD_FORM', formData)
  },

  /**
   * Reset form
   */
  resetForm({ commit }) {
    commit('RESET_UPLOAD_FORM')
    commit('SET_FILE_VALIDATION', { isValid: true, errors: [] })
  },

  /**
   * Set filters
   */
  setFilters({ commit }, filters) {
    commit('SET_CREATIVES_FILTERS', filters)
  },

  /**
   * Set page
   */
  setPage({ commit }, page) {
    commit('SET_CREATIVES_PAGE', page)
  },

  /**
   * Set page size
   */
  setPageSize({ commit }, pageSize) {
    commit('SET_CREATIVES_PAGE_SIZE', pageSize)
  },

  /**
   * Set sort
   */
  setSort({ commit }, { sortBy, sortDesc }) {
    commit('SET_CREATIVES_SORT', { sortBy, sortDesc })
  },

  /**
   * Bulk delete creatives
   */
  async bulkDeleteCreatives({ commit }, creativeIds) {
    commit('BULK_DELETE_CREATIVES_REQUEST')

    try {
      await Promise.all(
        creativeIds.map(id => CreativeService.deleteCreative(id)),
      )
      commit('BULK_DELETE_CREATIVES_SUCCESS', creativeIds)
    } catch (error) {
      commit('BULK_DELETE_CREATIVES_FAILURE', error)
      throw error
    }
  },

  /**
   * Bulk resubmit creatives for pre-review
   */
  async bulkResubmitCreatives({ commit }, { creativeIds, preReviewData }) {
    commit('BULK_RESUBMIT_CREATIVES_REQUEST')

    try {
      const results = await Promise.all(
        creativeIds.map(id => CreativeService.submitPreReview({
          creative_id: id,
          ...preReviewData,
        })),
      )

      const processedResults = results.map(({ data }, index) => ({
        creative_id: creativeIds[index],
        ...getResponse(data),
      }))

      commit('BULK_RESUBMIT_CREATIVES_SUCCESS', processedResults)
      return processedResults
    } catch (error) {
      commit('BULK_RESUBMIT_CREATIVES_FAILURE', error)
      throw error
    }
  },

  /**
   * Update creative pre-review status
   */
  updateCreativePreReviewStatus({ commit }, { creativeId, preReviewStatus, rejectReasons }) {
    commit('UPDATE_CREATIVE_PRE_REVIEW_STATUS', {
      creativeId,
      preReviewStatus,
      rejectReasons,
    })
  },

  /**
   * Get creative details
   */
  async getCreativeDetails(context, creativeId) {
    const { data } = await CreativeService.getCreativeDetails(creativeId)
    const creative = getResponse(data)
    return creative
  },

  /**
   * Submit creatives for pre-review
   */
  async submitCreativesForReview({ commit, state }, params) {
    commit('SUBMIT_PRE_REVIEW_REQUEST')

    try {
      const { advertiserId } = state.uploadForm
      if (!advertiserId) {
        throw new Error('No advertiser account selected')
      }

      const reviewParams = {
        advertiser_id: advertiserId,
        creatives: params.creatives, // Use the new creatives parameter
        ad_text: params.ad_text,
        landing_page_url: params.landing_page_url,
        location: params.location || 'US',
        is_ecommerce: params.is_ecommerce || false,
      }

      const { data } = await PreReviewService.submitCreativesForReview(reviewParams)
      const result = getResponse(data)

      commit('SUBMIT_PRE_REVIEW_SUCCESS', result)
      return result
    } catch (error) {
      commit('SUBMIT_PRE_REVIEW_FAILURE', error)
      throw error
    }
  },

  /**
   * Get pre-review task status
   */
  async getPreReviewTaskStatus({ commit }, { taskId, advertiserId }) {
    const { data } = await PreReviewService.getTaskStatus({
      task_id: taskId,
      advertiser_id: advertiserId,
    })
    const result = getResponse(data)

    commit('UPDATE_PRE_REVIEW_TASK_STATUS', {
      taskId,
      status: result.status,
      materials: result.materials,
    })

    return result
  },

  /**
   * Get pre-review task history
   */
  async fetchPreReviewHistory({ commit, state }, params = {}) {
    commit('FETCH_PRE_REVIEW_HISTORY_REQUEST')

    try {
      const { advertiserId } = state.uploadForm
      if (!advertiserId) {
        throw new Error('No advertiser account selected')
      }

      const { data } = await PreReviewService.getTaskHistory({
        advertiser_id: advertiserId,
        page: params.page || 1,
        size: params.size || 10,
        status: params.status,
      })

      const result = getResponse(data)
      commit('FETCH_PRE_REVIEW_HISTORY_SUCCESS', {
        tasks: result.tasks || [],
        pagination: result.pagination || {},
      })

      return result
    } catch (error) {
      commit('FETCH_PRE_REVIEW_HISTORY_FAILURE', error)
      throw error
    }
  },

  /**
   * Set upload progress
   */
  setUploadProgress({ commit }, progress) {
    commit('UPLOAD_CREATIVE_PROGRESS', progress)
  },

  /**
   * Set current upload
   */
  setCurrentUpload({ commit }, upload) {
    commit('UPLOAD_CREATIVE_SUCCESS', upload)
  },
}
